import * as React from 'react'
import * as ReactDOM from 'react-dom'
import * as ReactJSXRuntime from 'react/jsx-runtime'
import * as Sentry from '@sentry/react'

// Defined (as true) by the rf-components-from-cdn plugin in vite.config.base.ts, which runs
// for every build (`build-dev` and `build-prod` alike). Only the dev server leaves the
// identifier undefined, and there the loader no-ops and the linked npm copy is used.
declare const __RF_COMPONENTS_FROM_CDN__: boolean | undefined

const CDN_BASE = 'https://cdn.renderforest.com/rf-components'

/*
  Where a non-production page gets the bundle, in order.

  1. `<WEB_PATH>/static/rf-components/index.js` — published to each environment by
     rf-components' own development-N pipeline (and by `pnpm publish-local` from a laptop),
     exactly as gen-ai-flow, media_lib, rf-auth and upgrade-popup are published. Unversioned
     and overwritten by each deploy, so a development environment tracks whatever branch was
     deployed to it and nothing here needs bumping.
  2. `latest/` on the CDN — the newest RELEASE, used only when (1) has not been published to
     this environment. It keeps dev environments working before the pipeline exists; once it
     does, (1) always answers first and this is never reached.

  Both carry a `?t=` cache-buster. It is not optional for (2): the deploy uploads `latest/`
  with `max-age=0, must-revalidate` but the CDN rewrites that to `max-age=31536000`, which
  would pin an environment to whatever copy it cached first. (1) gets one for the same
  reason — it is overwritten in place. Versioned URLs never do; those really are immutable.
*/
const STATIC_PATH = '/static/rf-components/index.js'
const LATEST_URL = `${CDN_BASE}/latest/index.js`

/*
  Non-production resolution is deliberately the same shape as production: the bundle comes
  from the CDN, so QA exercises the real path on a development environment. Flip
  PREFER_ENV_BUNDLE to true to take it from this environment's own /static build instead
  (the "local build" path) — that constant is the only line to change.
*/
const PREFER_ENV_BUNDLE: boolean = false

// Hosts treated as non-production. The unversioned bundles above and the localStorage
// override are honored only here, never on a production page.
const LOCAL_HOST_PATTERN =
  /^(localhost|127\.0\.0\.1|t\.renderforest\.com|.+\.local\.renderforest\.com)$/

// Same shape website-front-end validates before injecting the pin; also keeps a crafted
// ?rf_version= from turning into an arbitrary script path on the CDN host.
const VERSION_PATTERN = /^\d+\.\d+\.\d+(?:-[\w.-]+)?$/

const validVersion = (value: string | null | undefined): string | null =>
  value && VERSION_PATTERN.test(value) ? value : null

/*
  A UMD whose factory throws part-way still fires `load`, and still leaves behind the
  object it had already assigned to window.RfComponents — with nothing on it. Accepting
  that as success hands `undefined` to the import shim, which React rejects with error
  #306 and takes the page down, so require real exports.
*/
const hasExports = (value: unknown): boolean =>
  !!value && Object.getOwnPropertyNames(value).length > 0

/*
  Which head-injected styled-components tags predate the rf-components CDN copy.

  The embedded mount mirrors head-injected rf-components rules into its shadow root
  (src/embed/rf-components-style-mirror.ts), and it has to copy that copy's rules and
  NOTHING else. The host page — the homepage — server-renders its own styled-components into
  the head as `style[data-styled]` tags that DO have readable textContent (ServerStyleSheet
  emits text, unlike the CSSOM injection styled-components uses on the client), and those
  include createGlobalStyle rules, which apply inside a shadow root. Mirroring them puts the
  host's resets and typography on top of the editor's own styling.

  A WeakSet, so a tag the host later removes is not retained here.
*/
const foreignStyledTags = new WeakSet<Element>()

const STYLED_TAG_SELECTOR = 'style[data-styled]'

/**
 * Records every styled-components tag currently in the head as "not ours". Safe to call
 * repeatedly, and only meaningful before the CDN copy has injected anything — see the call
 * sites for why each one is such a moment.
 */
export const markForeignStyledTags = (): void => {
  if (typeof document === 'undefined') return

  document.head
    .querySelectorAll(STYLED_TAG_SELECTOR)
    .forEach(node => foreignStyledTags.add(node))
}

export const isForeignStyledTag = (node: Element): boolean =>
  foreignStyledTags.has(node)

/*
  The host's server-rendered stylesheet is in the head before this module evaluates, and the
  CDN copy cannot have injected anything yet — its script is appended by this very module.
  Marking here rather than only at load time keeps the mirror correct on the paths where the
  load never happens at all (no pin on the page, or an already-captured copy), where every
  tag in the head is by definition foreign.
*/
markForeignStyledTags()

/**
 * Where to fetch the UMD bundle from, highest priority first:
 *
 * 1. `rf_components_cdn_base` in localStorage (non-prod hosts only) — the escape hatch for
 *    an unreleased bundle: point it at a local `pnpm serve:cdn`.
 * 2. `?rf_version=<semver>` — an explicit pin a tester typed, so any released build can be
 *    QA'd on any environment.
 * 3. Non-prod with no pin — this environment's own `/static` bundle, falling back to the
 *    CDN's `latest/` release when the environment has none. This is why a development
 *    environment needs neither the `rf_components_version` system option nor any
 *    per-repo version constant.
 * 4. Production — `window.rf_components_version`, injected by the host page from the
 *    `rf_components_version` system option. No default here: on production a missing pin
 *    must render nothing, never some other version nobody asked for.
 *
 * Returns null only in case 4 with no pin; the caller then warns and renders nothing.
 */
const resolveScriptUrls = (): string[] => {
  const isNonProd = LOCAL_HOST_PATTERN.test(window.location.hostname)
  const queryVersion = validVersion(
    new URLSearchParams(window.location.search).get('rf_version')
  )

  if (isNonProd) {
    try {
      const override = window.localStorage.getItem('rf_components_cdn_base')
      if (override) {
        const base = override.replace(/\/+$/, '')

        return [
          queryVersion ? `${base}/${queryVersion}/index.js` : `${base}/index.js`
        ]
      }
    } catch {
      // storage unavailable — fall through to the resolution below
    }
  }

  if (queryVersion) return [`${CDN_BASE}/${queryVersion}/index.js`]

  if (isNonProd) {
    const bust = `?t=${Date.now()}`

    if (PREFER_ENV_BUNDLE) {
      const webPath = (window.WEB_PATH || '').replace(/\/+$/, '')

      return [`${webPath}${STATIC_PATH}${bust}`, `${LATEST_URL}${bust}`]
    }

    /*
      The CDN, resolved as production resolves it: this environment's pin when the host
      page injects one, otherwise the newest release. One candidate only — a miss must
      render nothing rather than quietly run some other version.
    */
    const environmentPin = validVersion(window.rf_components_version)

    return [
      environmentPin
        ? `${CDN_BASE}/${environmentPin}/index.js`
        : `${LATEST_URL}${bust}`
    ]
  }

  const pinned = validVersion(window.rf_components_version)

  return pinned ? [`${CDN_BASE}/${pinned}/index.js`] : []
}

/*
  Diagnostics.

  Degrading to "renders nothing" is what stops a bad pin from taking the editor down — and
  it is also what makes a bad pin invisible, because nothing throws and nobody is paged.
  Every outcome is recorded in three places:

  - `window.__RF_COMPONENTS_LOAD_STATUS__` — one record per app on the page, shared verbatim
    with every other consumer. This is what QA and support read to tell "no pin on the page"
    apart from "the pin names a broken build".
  - a `rf-components:load` event on window — lets monitoring be wired ONCE, on the host page,
    for every consumer at the same time.
  - Sentry, which `src/index.tsx` initializes before this loader runs (`import './sentry'` is
    its first line), plus the console.
*/
type TLoadOutcome =
  /** The bundle evaluated and published its exports. */
  | 'loaded'
  /** A production page with no `rf_components_version` to load. */
  | 'no-pin'
  /** Every candidate URL failed, timed out, or produced nothing. */
  | 'unavailable'

const report = (
  outcome: TLoadOutcome,
  detail: Record<string, unknown> = {}
) => {
  const record = {
    app: 'v-editor',
    outcome,
    requested: window.rf_components_version ?? null,
    /*
      Set by the library itself as it evaluates, so this is "what actually loaded" against
      the pin above. Loads are serialized page-wide (see serializeLoad), so by the time we
      read it, it belongs to our own copy rather than to whichever app raced us.
    */
    loaded: window.RF_COMPONENTS_LOADED_VERSION ?? null,
    embedded: !!window.V_EDITOR_EMBEDDED_MODE,
    ...detail
  }

  const status = (window.__RF_COMPONENTS_LOAD_STATUS__ ??= [])
  status.push(record)

  const mismatched =
    outcome === 'loaded' &&
    record.requested &&
    record.loaded &&
    record.requested !== record.loaded

  if (outcome !== 'loaded') {
    // eslint-disable-next-line no-console
    console.error('[rf-components] load degraded', record)
    Sentry.captureMessage('rf-components load degraded', {
      level: 'error',
      extra: record
    })
  } else if (mismatched) {
    // eslint-disable-next-line no-console
    console.warn(
      '[rf-components] version mismatch — the page ran a build it did not pin',
      record
    )
    Sentry.captureMessage('rf-components version mismatch', {
      level: 'warning',
      extra: record
    })
  }

  try {
    window.dispatchEvent(
      new CustomEvent('rf-components:load', { detail: record })
    )
  } catch {
    // No CustomEvent constructor — the record above is still readable.
  }
}

/*
  How long one candidate URL gets before it is treated as a miss. Deliberately shorter than
  TURN_TIMEOUT_MS below, so a hung load releases the shared chain by itself rather than
  leaving the next app to time out waiting for it.
*/
const LOAD_TIMEOUT_MS = 8000

/*
  Every rf-components load on the page takes its turn in one shared chain.

  A loaded copy of the UMD binds whatever React family the window globals hold as it
  evaluates, so each app has to load its OWN copy against its OWN React — and that cannot
  be done concurrently. `window.React` is a single slot: two apps setting it around their
  own script append can hand one app's copy the other app's React, which throws
  "Cannot read properties of null (reading 'useState')" the first time it renders.

  The key is shared verbatim by every rf-components consumer (homepage, watch-page,
  v-editor, video-editor, ai-flow-spa, gen-ai-flow). Renaming it in one repo alone silently
  brings the race back.
*/
// A load that never settles — a script that fires neither `load` nor `error` — must not
// strand every other app's UI on the page, so a turn waits at most this long for the one
// ahead of it before going anyway.
const TURN_TIMEOUT_MS = 10000

const serializeLoad = <T>(load: () => Promise<T>): Promise<T> => {
  const host = window as unknown as {
    __RF_COMPONENTS_LOAD_CHAIN__?: Promise<unknown>
  }
  const ahead = host.__RF_COMPONENTS_LOAD_CHAIN__ ?? Promise.resolve()

  const turn = new Promise<void>(resolve => {
    const timer = window.setTimeout(resolve, TURN_TIMEOUT_MS)
    const go = () => {
      window.clearTimeout(timer)
      resolve()
    }

    ahead.then(go, go)
  })

  const result = turn.then(load)

  // A rejected link would strand every later turn, so what is stored never rejects.
  host.__RF_COMPONENTS_LOAD_CHAIN__ = result.then(
    () => undefined,
    () => undefined
  )

  return result
}

/**
 * Loads the rf-components UMD bundle from the CDN and captures it as
 * window.__V_EDITOR_RF_COMPONENTS__, which is what the bare '@renderforest/rf-components'
 * import resolves to in production builds (see vite.config.prod.ts).
 *
 * Version resolution: ?rf_version= query (QA) → window.rf_components_version (injected by
 * the host page from the rf_components_version system option).
 *
 * The copy is deliberately app-scoped rather than left on window.RfComponents: the UMD
 * binds whatever React family the window globals hold at evaluation time, and the embedded
 * editor runs on a host page (the homepage) that has its OWN React and loads its OWN
 * rf-components copy against it. A copy bound to the host's React would crash when
 * rendered by this app's React, so the globals are set just for this load and restored
 * right after. (If two loaders on one page ever interleave between set and evaluate, one
 * copy binds the wrong React — accepted: this loader runs at editor boot, long after the
 * host page's own loader has finished.)
 *
 * Never rejects: on any failure it resolves after a console warning, and the import shim
 * renders nothing in place of the components.
 */
const loadRfComponentsOnce = (): Promise<void> => {
  if (
    typeof __RF_COMPONENTS_FROM_CDN__ === 'undefined' ||
    !__RF_COMPONENTS_FROM_CDN__
  ) {
    return Promise.resolve()
  }

  if (window.__V_EDITOR_RF_COMPONENTS__) return Promise.resolve()

  const urls = resolveScriptUrls()

  if (!urls.length) {
    report('no-pin')

    return Promise.resolve()
  }

  /*
    Every `style[data-styled]` already in the head belongs to somebody else — the host page's
    server-rendered stylesheet above all — and the mirror must never carry those into the
    shadow root. Marked here because this is the real "before rf-components existed on this
    page" moment: the CDN copy's styled-components injects lazily, on the first render of
    each component, so its own tags appear well after this point and are exactly the ones
    NOT marked. Marking at mirror-start instead would break on a remount, by which time the
    copy's own tags are already in the head.
  */
  markForeignStyledTags()

  if (window.V_EDITOR_EMBEDDED_MODE) {
    /*
      Force TEXT-mode style injection for the CDN copy's bundled styled-components (it reads
      this global once, at script evaluation). The embedded mount mirrors head-injected
      rf-components rules into the shadow root by reading <style> textContent — CSSOM-injected
      rules would be invisible to it. See src/embed/rf-components-style-mirror.ts. Already
      initialized styled-components copies on the page (the host's, this app's) are unaffected.
    */
    window.SC_DISABLE_SPEEDY = true
  }

  const previous = {
    React: window.React,
    ReactDOM: window.ReactDOM,
    ReactJSXRuntime: window.ReactJSXRuntime,
    RfComponents: window.RfComponents,
    SC_DISABLE_SPEEDY: window.SC_DISABLE_SPEEDY
  }

  // The UMD resolves react / react-dom / react/jsx-runtime from these globals at
  // evaluation time — they must be set before the script runs.
  window.React = React
  window.ReactDOM = ReactDOM
  window.ReactJSXRuntime = ReactJSXRuntime

  const restore = () => {
    window.React = previous.React
    window.ReactDOM = previous.ReactDOM
    window.ReactJSXRuntime = previous.ReactJSXRuntime
    window.RfComponents = previous.RfComponents
    /*
      Put back for the same reason as the React globals: styled-components reads this once,
      when an instance initializes. Left set to true it silently forces TEXT-mode injection
      on every styled-components copy the HOST initializes afterwards — whose tags then have
      readable textContent, get picked up by the mirror, and carry host CSS into the shadow
      root, defeating the isolation it exists to provide.
    */
    window.SC_DISABLE_SPEEDY = previous.SC_DISABLE_SPEEDY
  }

  return new Promise(resolve => {
    /*
      Candidates are tried in order: a failed request falls through to the next one, and
      only the last failure gives up. `restore()` runs once, at the end — the React globals
      have to still be in place while any later candidate evaluates.
    */
    const attempt = (index: number) => {
      const src = urls[index]
      const isLast = index === urls.length - 1
      /*
        What this script produces has to be a NEW object. Another app's copy may already
        be sitting on the global — adopting that one means rendering components bound to
        that app's React, which is exactly the crash this loader must not cause.
      */
      const before = window.RfComponents

      const next = () => {
        if (!isLast) {
          attempt(index + 1)
          return
        }
        restore()
        report('unavailable', { src })
        resolve()
      }

      const script = document.createElement('script')

      /*
        `load`, `error` and the timeout race each other; the first to land wins and the rest
        are ignored. Without this, a `load` arriving after the timeout would re-enter the
        handler with the globals already restored and capture a copy bound to the wrong
        React.
      */
      let settled = false
      let timer = 0

      const settle = (finish: () => void) => {
        if (settled) return
        settled = true
        window.clearTimeout(timer)

        finish()
      }

      /*
        `onerror` covers a refused connection, a 404 and a TLS failure, and fires quickly. It
        does NOT cover a request that is accepted and then hangs: that fires neither event,
        and this app's entire boot is gated on this loader (src/index.tsx), so without a
        bound the editor would never mount at all.
      */
      timer = window.setTimeout(() => {
        settle(() => {
          // Detached and removed so a script that eventually arrives cannot re-enter the
          // handler; the `before` guard is what then protects the next app from adopting it.
          script.onload = null
          script.onerror = null
          script.remove()
          next()
        })
      }, LOAD_TIMEOUT_MS)

      script.src = src
      script.async = true
      script.onload = () =>
        settle(() => {
          // A UMD that threw part-way still fires `load` and leaves an empty object behind,
          // and an unchanged global means this script produced nothing of its own.
          if (
            window.RfComponents === before ||
            !hasExports(window.RfComponents)
          ) {
            next()
            return
          }
          window.__V_EDITOR_RF_COMPONENTS__ = window.RfComponents
          restore()
          report('loaded')
          resolve()
        })
      script.onerror = () => settle(next)
      document.head.appendChild(script)
    }

    attempt(0)
  })
}

/**
 * Public entry. Serialized against every other rf-components load on the page — see
 * serializeLoad above for why concurrent loads corrupt each other's React binding.
 */
export const loadRfComponents = (): Promise<void> =>
  serializeLoad(loadRfComponentsOnce)
